import axios from "axios";

import type { IColorPalette } from "models/colorPalette";

export const userServiceRoot = process.env.REACT_APP_USER_SERVICE;
const colorServiceRoot = process.env.REACT_APP_COLOR_SERVICE;

interface UserSettingT {
  id?: string;
}

export async function getMySettings(group) {
  const response = await axios.get(`${userServiceRoot}/user/setting/my/${group}`);
  if (response.status !== 200) {
    return null;
  }
  return response.data;
}
export async function getPublicSettings(group) {
  const response = await axios.get(`${userServiceRoot}/user/setting/public/${group}`);
  if (response.status !== 200) {
    return null;
  }
  return response.data;
}

export async function getOrganizationTypes(onSuccess, onError): Promise<void> {
  axios.get(`${userServiceRoot}/organization/org-types`).then(onSuccess).catch(onError);
}

export async function getOrganizationSizes(onSuccess, onError): Promise<void> {
  axios.get(`${userServiceRoot}/organization/org-sizes`).then(onSuccess).catch(onError);
}

export async function getOrganizationPalettes(): Promise<Nullable<IColorPalette[]>> {
  const response = await axios.get(`${colorServiceRoot}/palette`);
  if (response.status !== 200) {
    return null;
  }
  return response.data;
}

export async function updateUserSetting<T extends UserSettingT>(
  setting: string,
  value: T
): Promise<T> {
  if (value.id) {
    const response = await axios.put(
      `${userServiceRoot}/user/setting/${setting}/${value.id}`,
      value
    );
    if (response.status !== 200) {
      throw "Unable to update setting.";
    }
    return response.data;
  } else {
    // D3 — strip a null/undefined id from the request body before POSTing. A resolved
    // (org-default) value carries an explicit `id: null` (see the resolver, D3); sending
    // that verbatim reaches UserSettingCollection.SetOrReplaceSetting, which uses the id
    // directly as a dictionary key and throws on null. An absent `id` property survives
    // fine — only the explicit null/undefined is fatal. Only strip null/undefined; a real
    // id (or any other falsy-but-defined value a future caller passes) is left untouched,
    // since this branch is otherwise unreachable for a truthy id.
    const { id, ...rest } = value;
    const body = id === null || id === undefined ? rest : value;

    const response = await axios.post(`${userServiceRoot}/user/setting/${setting}`, body);
    if (response.status !== 200) {
      throw "Unable to update setting.";
    }
    return response.data;
  }
}

/**
 * Always POSTs (creates) a new user setting entry regardless of whether `id` is
 * present. Use this when the caller determines "this entityKind has never been
 * saved before" and wants to bypass the PUT branch in `updateUserSetting`.
 */
export async function createUserSetting<T extends UserSettingT>(
  setting: string,
  value: T
): Promise<T> {
  const response = await axios.post(`${userServiceRoot}/user/setting/${setting}`, value);
  if (response.status !== 200) {
    throw "Unable to create setting.";
  }
  return response.data;
}

export async function deleteUserSetting(setting: string, id: string): Promise<void> {
  const response = await axios.delete(`${userServiceRoot}/user/setting/${setting}/${id}`);
  if (response.status !== 200) {
    throw "Unable to delete setting.";
  }
}

export async function getUserSetting<T extends UserSettingT>(
  setting: string
): Promise<T> {
  const response = await axios.get<T[]>(`${userServiceRoot}/user/setting/${setting}`);
  if (response.status !== 200) {
    throw "Unable to fetch user setting.";
  }
  if (response.data.length) {
    return response.data[0] as T;
  }
  return null;
}
